import React from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  GitCompareIcon, Mail01Icon, McpServerIcon, PlugSocketIcon, ViberIcon, WhatsappIcon
} from "@hugeicons/core-free-icons";
import {sidebarMenuItems} from "../project/flow/initialState";
import {RegistryDashboardPage} from "./dashboard/RegistryDashboardPage";
import {RegistrySettingsPage} from "./settings/RegistrySettingsPage";
import {RegistrySipConfigurationPage} from "./sip/RegistrySipConfigurationPage";
import {RegistrySupervisionPage} from "./supervision/RegistrySupervisionPage";
import {RegistryCampaignsPage} from "./campaigns/RegistryCampaignsPage";
import {RegistryMessageAnnotationsPage} from "./message-annotation/RegistryMessageAnnotationsPage";
import {RegistryVocabularyPage} from "./vocabulary/RegistryVocabularyPage";
import {RegistryDesignerPage} from "./designer/RegistryDesignerPage";
import {RegistryMCPDiscoveryPage} from "./mcp-discovery/RegistryMCPDiscoveryPage";
import {RegistryKnowledgeManagerPage} from "./knowledge-manager/RegistryKnowledgeManagerPage";
import {RegistryVersionControlPage} from "./version-control/RegistryVersionControlPage";
import {RegistryMailConnectorPage} from "./connectors/RegistryMailConnectorPage";
import {RegistryWhatsappConnectorPage} from "./connectors/RegistryWhatsappConnectorPage";
import {RegistryViberConnectorPage} from "./connectors/ConnectorComingSoon";
// The registry project uses the same menu as the flow layout. Legacy items build their links from a
// Firebase project (`/project/{id}/...`), so they are converted to paths relative to
// `/registry-project/{registryProjectId}/{subProjectId}`.
const PLACEHOLDER_ID = "__registry_project__";

const toRelativePath = (item) => item.to({id: PLACEHOLDER_ID})
  .replace(`/project/${PLACEHOLDER_ID}`, "")
  .replace(/^\//, "");

export const joinPath = (basePath, path) => path ? `${basePath}/${path}` : basePath;

// Registry-only changes to shared menu entries, by path. SIP Configuration holds the dialplan (ADMIN) and the agent
// groups (ADMIN / MANAGER): the page shows each role its own tabs.
const registryOverrides = {
  "dialplan": {label: "SIP Configuration", roles: ["ADMIN", "MANAGER"]},
};

const toRegistryItem = (item) => ({
  ...item,
  ...registryOverrides[item.to ? toRelativePath(item) : undefined],
  path: item.to ? toRelativePath(item) : undefined,
  // menu context: {basePath, project} where project is the registry ProjectDTO
  to: item.to ? (context) => joinPath(context?.basePath, toRelativePath(item)) : undefined,
  // legacy checks read Firebase settings, so map the registry project onto that shape
  disabled: item.disabled
    ? (context) => item.disabled({settings: {organization: context?.project?.organizationId}})
    : undefined,
  submenu: item.submenu?.map(toRegistryItem),
});

const hi = (icon) => <HugeiconsIcon icon={icon} size={20} strokeWidth={1.5} color="currentColor"/>;

// Pages only registry projects have (the flow layout has no route for them), in the legacy shape so they go through
// toRegistryItem like the rest; placed before Settings
const registryOnlyMenuItems = [
  {
    label: "Connectors",
    icon: hi(PlugSocketIcon),
    roles: ["ADMIN", "MANAGER"],
    submenu: [
      {
        label: "Mail Connector",
        icon: hi(Mail01Icon),
        to: (project) => `/project/${project?.id}/connectors/mail`,
        roles: ["ADMIN", "MANAGER"]
      },
      {
        label: "Viber Connector",
        icon: hi(ViberIcon),
        to: (project) => `/project/${project?.id}/connectors/viber`,
        roles: ["ADMIN", "MANAGER"]
      },
      {
        label: "WhatsApp Connector",
        icon: hi(WhatsappIcon),
        to: (project) => `/project/${project?.id}/connectors/whatsapp`,
        roles: ["ADMIN", "MANAGER"]
      },
      {
        label: "MCP",
        icon: hi(McpServerIcon),
        to: (project) => `/project/${project?.id}/mcp-registry`,
        roles: ["ADMIN", "MANAGER"]
      },
    ]
  },
  {
    label: "Version control",
    icon: hi(GitCompareIcon),
    to: (project) => `/project/${project?.id}/version-control`,
    roles: ["ADMIN", "MANAGER"]
  },
];

const baseMenuItems = sidebarMenuItems.filter(item => item.label !== "MCP Registry");
const settingsIndex = baseMenuItems.findIndex(item => item.label === "Settings");
const insertAt = settingsIndex < 0 ? baseMenuItems.length : settingsIndex;

export const registrySidebarMenuItems = [
  ...baseMenuItems.slice(0, insertAt),
  ...registryOnlyMenuItems,
  ...baseMenuItems.slice(insertAt),
].map(toRegistryItem);

// One route per menu entry (submenus flattened); "" is the index (Dashboard)
// A page can have several menu entries (e.g. Vocabulary: top level for agents, under Settings for admins and
// managers); they share one route that allows the roles of all of them
export const registryProjectRoutes = Object.values(registrySidebarMenuItems
  .flatMap(item => item.submenu ? item.submenu : [item])
  .filter(item => item.path !== undefined)
  .reduce((routes, {label, path, roles}) => {
    const route = routes[path];
    routes[path] = route
      ? {...route, roles: [...new Set([...route.roles, ...roles])]}
      : {label, path, roles};
    return routes;
  }, {}));

// Pages rebuilt on the Project API, by route path ("" is the dashboard). Routes without an entry show a
// placeholder. Pages read the project, selected sub project and role with useRegistryProject().
export const registryPages = {
  "": RegistryDashboardPage,
  "settings": RegistrySettingsPage,
  "dialplan": RegistrySipConfigurationPage,
  "supervision": RegistrySupervisionPage,
  "campaigns": RegistryCampaignsPage,
  "message-annotations": RegistryMessageAnnotationsPage,
  "vocabulary": RegistryVocabularyPage,
  "flow": RegistryDesignerPage,
  "mcp-registry": RegistryMCPDiscoveryPage,
  "knowledge-manager": RegistryKnowledgeManagerPage,
  "version-control": RegistryVersionControlPage,
  "connectors/mail": RegistryMailConnectorPage,
  "connectors/viber": RegistryViberConnectorPage,
  "connectors/whatsapp": RegistryWhatsappConnectorPage,
};
